import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { useGetStoreSalesQuery } from "../../slices/storeSalesApiSlice";
import { useGetStoreExpensesQuery } from "../../slices/storeExpensesApiSlice";
import { useGetStoreIncomesQuery } from "../../slices/storeIncomesApiSlice";
import { useGetStoreDepositsQuery } from "../../slices/storeDepositsApiSlice";
import { useGetStoreSuppliesQuery } from "../../slices/storeSuppliesApiSlice";

export default function AllTotal() {
  const today = new Date().toISOString().split("T")[0];

  // Fetch data using RTK Query
  const { data: storeSalesData, isLoading: storeSalesLoading } = useGetStoreSalesQuery();
  const { data: storeExpensesData, isLoading: storeExpensesLoading } = useGetStoreExpensesQuery({
    startDate: "1900-01-01", // Placeholder for earliest date
    endDate: today,
  });
  const { data: storeIncomesData, isLoading: storeIncomesLoading } = useGetStoreIncomesQuery({
    startDate: "1900-01-01", // Placeholder for earliest date
    endDate: today,
  });
  const { data: storeDepositsData, isLoading: storeDepositsLoading } = useGetStoreDepositsQuery({
    startDate: "1900-01-01", // Placeholder for earliest date
    endDate: today,
  });
  const { data: storeSuppliesData, isLoading: storeSuppliesLoading } = useGetStoreSuppliesQuery();

  const [totals, setTotals] = useState({
    totalStoreSales: 0,
    totalStoreExpenses: 0,
    totalStoreIncomes: 0,
    totalStoreDeposits: 0,
    totalPurchases: 0,
    netCash: 0,
    difference: 0,
    cashInHand: 0,
  });

  useEffect(() => {
    if (
      !storeSalesLoading &&
      !storeExpensesLoading &&
      !storeIncomesLoading &&
      !storeDepositsLoading &&
      !storeSuppliesLoading
    ) {
      // Calculate general totals
      const totalStoreSales = storeSalesData?.storeSales?.reduce((sum, storeSale) => sum + Number(storeSale.totalPrice || 0), 0) || 0;

      const totalStoreExpenses = storeExpensesData?.storeExpenses?.reduce(
        (sum, storeExpense) => sum + Number(storeExpense.amount || 0),
        0
      ) || 0;

      const totalStoreIncomes = storeIncomesData?.storeIncomes?.reduce(
        (sum, storeIncome) => sum + Number(storeIncome.amount || 0),
        0
      ) || 0;

      const totalStoreDepositsRaw = storeDepositsData?.storeDeposits?.reduce(
        (sum, storeDeposit) => sum + Number(storeDeposit.amount || 0),
        0
      ) || 0;
      const totalStoreDeposits = totalStoreDepositsRaw > 0 ? totalStoreDepositsRaw * -1 : totalStoreDepositsRaw * +1;

      const totalPurchasesRaw = storeSuppliesData?.storeSupplies?.reduce(
        (sum, storeSupply) => sum + Number(storeSupply.cardCost || 0),
        0
      ) || 0;
      const totalPurchases = totalPurchasesRaw * -1; // Always multiply by -1

      // Calculate NetCash, Difference, and CashInHand
      const netCash = totalStoreSales + totalStoreExpenses;
      const difference = totalStoreIncomes - netCash;
      const cashInHand = netCash + totalStoreDeposits + totalPurchases;

      // Update totals state
      setTotals({
        totalStoreSales: totalStoreSales.toFixed(2),
        totalStoreExpenses: totalStoreExpenses.toFixed(2),
        totalStoreIncomes: totalStoreIncomes.toFixed(2),
        totalStoreDeposits: totalStoreDeposits.toFixed(2),
        totalPurchases: totalPurchases.toFixed(2),
        netCash: netCash.toFixed(2),
        difference: difference.toFixed(2),
        cashInHand: cashInHand.toFixed(2),
      });
    }
  }, [
    storeSalesData,
    storeExpensesData,
    storeIncomesData,
    storeDepositsData,
    storeSuppliesData,
    storeSalesLoading,
    storeExpensesLoading,
    storeIncomesLoading,
    storeDepositsLoading,
    storeSuppliesLoading,
  ]);

  // Export totals to Excel
  const exportToExcel = () => {
    const exportData = [
      { Header: "Total StoreSales", Value: totals.totalStoreSales },
      { Header: "Total StoreExpenses", Value: totals.totalStoreExpenses },
      { Header: "Total StoreIncomes", Value: totals.totalStoreIncomes },
      { Header: "Total StoreDeposits", Value: totals.totalStoreDeposits },
      { Header: "Total Purchases", Value: totals.totalPurchases },
      { Header: "Net Cash", Value: totals.netCash },
      { Header: "Difference", Value: totals.difference },
      { Header: "Cash In Hand", Value: totals.cashInHand },
    ];

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AllTotals");
    XLSX.writeFile(workbook, "AllTotals.xlsx");
  };

  return (
    <div className="all-total">
      <h1>All Collections Total</h1>

      {storeSalesLoading ||
      storeExpensesLoading ||
      storeIncomesLoading ||
      storeDepositsLoading ||
      storeSuppliesLoading ? (
        <div>Loading data...</div>
      ) : (
        <>
          <table className="totals-table">
            <thead>
              <tr>
                <th>Header</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Total StoreSales</strong></td>
                <td>${totals.totalStoreSales}</td>
              </tr>
              <tr>
                <td><strong>Total StoreIncome and StoreExpenses</strong></td>
                <td>${totals.totalStoreExpenses}</td>
              </tr>
              <tr>
                <td><strong>Net Cash </strong></td>
                <td>${totals.netCash}</td>
              </tr>
              <tr>
                <td><strong>Total Cash Collected</strong></td>
                <td>${totals.totalStoreIncomes}</td>
              </tr>
              <tr>
                <td><strong>Difference</strong></td>
                <td>${totals.difference}</td>
              </tr>
              <tr>
                <td><strong>Net Bank</strong></td>
                <td>${totals.totalStoreDeposits}</td>
              </tr>
              <tr>
                <td><strong>Total Purchases</strong></td>
                <td>${totals.totalPurchases}</td>
              </tr>
              <tr>
                <td><strong>Cash In Hand</strong></td>
                <td>${totals.cashInHand}</td>
              </tr>
            </tbody>
          </table>

          <button onClick={exportToExcel} className="export-button">
            Export to Excel
          </button>
        </>
      )}
    </div>
  );
}